import { Suspense, useEffect, useState } from 'react';
import { Loader, UserDetails } from 'neon-design-system';
import { useAuth0, useAuth0Data } from 'neon-auth0';
import * as appcues from 'neon-appcues';
import * as packageJson from '../package.json';
import { Layout } from './components/Layout/Layout';
import Routes from './routes/Routes';
import { useSetupAppcues } from './hooks/useSetupAppcues';
import { useRouteChangeLogging } from './hooks/useRouteChangeLogging';
import { useSessionData } from './contexts/SessionData';
import { config } from './config';
import './App.css';
import { useSnackbar } from './providers/SnackbarProvider';
import { NeonUserIdProvider } from './providers/NeonUserIdProvider';
import { PermissionsProvider } from './providers/PermissionsProvider';
import { FiltersProvider } from './providers/FiltersProvider';
import { DialogContainer } from './components/DialogContainer/DialogContainer';
import jwt from 'jwt-decode';
import { useConciergeUserQuery } from './hooks/useConciergeUserQuery';
import { ContractLimitDialogProvider } from './sites/limits/providers/ContractLimitDialogProvider';
import { ManageLimitsProvider } from './sites/limits/providers/ManageLimitsProvider';
import { PaginationRequestProvider } from './providers/PaginationRequestProvider';
import { setSessionAndCallId } from './utils/axios';
import { setNeonUserId as setLoggerNeonUserId, logEvent } from './utils/logger';

const App = () => {
  const [neonUserId, setNeonUserId] = useState();
  const sessionId = useSessionData().sessionId;

  const { user } = useAuth0();
  const { snackbarContainer } = useSnackbar();
  const { accessToken } = useAuth0Data();

  const { data, isFetching } = useConciergeUserQuery();
  const accountChangeRequestEnabled = !!data?.accountChangeRequestEnabled;
  const riskLimitRequestEnabled = !!data?.riskLimitRequestEnabled;
  const accountOpeningFormEnabled = !!data?.accountOpeningFormEnabled;
  const cashMovementRequestsEnabled = !!data?.cashMovementsEnabled;
  const achPullRequestsEnabled = !!data?.achPullsEnabled;
  const bulkCashMovementRequestsEnabled = !!data?.bulkCashMovementsEnabled;

  setSessionAndCallId(sessionId);

  const getUserDetails = (user: any): UserDetails => ({
    name: (user?.given_name && user?.family_name && user?.given_name + ' ' + user?.family_name) || user?.name || user?.email || '',
    role: 'Client',
    sessionId
  });

  useEffect(() => {
    if (accessToken) {
      const decodedToken: any = jwt(accessToken);
      const propId = Object.keys(decodedToken).find((x) => x.includes('neonUserId'));
      if (propId && decodedToken[propId]) {
        setNeonUserId(decodedToken[propId]);
        setLoggerNeonUserId(decodedToken[propId]);
      }

      logEvent({
        event: 'session_started',
        clientWidth: window.innerWidth,
        clientHeight: window.innerHeight
      });
    }
  }, [accessToken]);

  useSetupAppcues(sessionId, config.env, config.version);
  useRouteChangeLogging();

  useEffect(() => {
    if (!isFetching) {
      appcues.identify({
        showRiskLimitLink: riskLimitRequestEnabled,
        showAccountChangeRequestLink: accountChangeRequestEnabled,
        newAccountSetupEnabled: accountOpeningFormEnabled
      });
    }
  }, [isFetching, riskLimitRequestEnabled, accountChangeRequestEnabled, accountOpeningFormEnabled]);

  if (isFetching) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<Loader key="loader" />}>
      {user && (
        <NeonUserIdProvider neonUserId={neonUserId}>
          <PermissionsProvider
            permissions={{
              accountChangeRequestEnabled,
              riskLimitRequestEnabled,
              accountOpeningFormEnabled,
              generalRequestsEnabled: true,
              cashMovementRequestsEnabled,
              achPullRequestsEnabled,
              bulkCashMovementRequestsEnabled
            }}
          >
            <FiltersProvider>
              <PaginationRequestProvider>
                <ManageLimitsProvider>
                  <ContractLimitDialogProvider>
                    <Layout appVersion={packageJson.version} userDetails={getUserDetails(user)}>
                      {snackbarContainer}
                      <DialogContainer />
                      <Routes />
                    </Layout>
                  </ContractLimitDialogProvider>
                </ManageLimitsProvider>
              </PaginationRequestProvider>
            </FiltersProvider>
          </PermissionsProvider>
        </NeonUserIdProvider>
      )}
    </Suspense>
  );
};

export default App;
